import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, MapPin, Edit, QrCode, Copy, Power, Printer, FileBarChart, Camera, History } from "lucide-react";

const EMPTY = { store_name: "", store_code: "", parish: "", town: "", address: "",
  telephone_number: "", email_address: "", timezone: "America/Jamaica", opening_time: "08:00",
  closing_time: "17:00", kiosk_identifier: "", uses_company_default_sla: true, is_active: true };

const POSTER_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;900&family=DM+Sans:wght@400;600&family=JetBrains+Mono&display=swap');
  *{margin:0;box-sizing:border-box}
  body{font-family:'DM Sans',sans-serif;color:#0A0A0A}
  .poster{padding:56px;text-align:center;min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center}
  .poster + .poster{page-break-before:always}
  .brand{font-family:'Barlow Condensed';font-weight:900;font-size:34px;letter-spacing:-1px}
  .store{text-transform:uppercase;letter-spacing:4px;color:#555;font-size:15px;margin-top:4px}
  h1{font-family:'Barlow Condensed';font-weight:900;font-size:64px;line-height:0.95;margin:40px 0 8px}
  p.sub{font-size:22px;color:#333;margin-bottom:32px}
  img{border:2px solid #0A0A0A;padding:14px;border-radius:8px}
  .code{font-family:'JetBrains Mono';font-size:15px;margin-top:14px;color:#555}
  ol{text-align:left;max-width:440px;margin:36px auto 0;font-size:20px;line-height:1.7}
  .foot{margin-top:40px;color:#888;font-size:14px}
  .rule{height:6px;background:#0A0A0A;width:80px;margin:24px auto}
  .sheet{padding:56px;text-align:center}
  .sheet h1{font-family:'Barlow Condensed';font-weight:900;font-size:56px;line-height:0.95;margin:12px 0 4px}
  .sheet h2{font-family:'Barlow Condensed';font-weight:900;font-size:26px;margin:8px 0;text-transform:uppercase;letter-spacing:2px}
  .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;max-width:720px;margin:0 auto}
  .metric{border:2px solid #0A0A0A;border-radius:8px;padding:18px 10px}
  .metric .mv{font-family:'Barlow Condensed';font-weight:900;font-size:38px;line-height:1}
  .metric .ml{font-size:12px;text-transform:uppercase;letter-spacing:1px;color:#555;margin-top:6px}
`;

export default function Locations() {
  const [stores, setStores] = useState([]);
  const [company, setCompany] = useState(null);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [kioskStore, setKioskStore] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [showSnapshots, setShowSnapshots] = useState(false);
  const [cmpA, setCmpA] = useState("");
  const [cmpB, setCmpB] = useState("");

  const load = () => {
    api.get("/stores").then((r) => setStores(r.data.stores)).catch(() => {});
    api.get("/company").then((r) => setCompany(r.data.company)).catch(() => {});
    api.get("/reports/spotlight/snapshots").then((r) => setSnapshots(r.data.snapshots)).catch(() => {});
  };
  useEffect(load, []);

  const fmtS = (s) => {
    if (s == null) return "—";
    const m = Math.floor(s / 60), r = Math.round(s % 60);
    return m ? `${m}m ${r}s` : `${r}s`;
  };

  const saveSnapshot = async (s) => {
    try {
      await api.post("/reports/spotlight/save", { store_id: s.id });
      toast.success(`Snapshot saved for ${s.store_name}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/stores/${editing}`, form);
      else await api.post("/stores", form);
      toast.success("Store saved"); setForm(null); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggle = async (s) => {
    try { await api.post(`/stores/${s.id}/toggle`); toast.success("Updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filtered = stores.filter((s) => filter === "all" || (filter === "active" ? s.is_active : !s.is_active));
  const kioskUrl = kioskStore && company
    ? `${window.location.origin}/kiosk/${company.company_code}/${kioskStore.store_code}` : "";

  const posterSection = (s) => {
    const url = `${window.location.origin}/kiosk/${company.company_code}/${s.store_code}`;
    const qr = `https://api.qrserver.com/v1/create-qr-code/?size=360x360&data=${encodeURIComponent(url)}`;
    return `<section class="poster">
      <div class="brand">${company.company_name}</div>
      <div class="store">${s.store_name}</div>
      <div class="rule"></div>
      <h1>Package<br/>Collection<br/>Check-In</h1>
      <p class="sub">Scan to check in, or use the on-site tablet</p>
      <img src="${qr}" width="320" height="320" />
      <div class="code">${url}</div>
      <div class="code">Setup code: ${company.company_code}-${s.store_code}</div>
      <ol>
        <li>Scan the QR code with your phone camera.</li>
        <li>Enter your <strong>KIN number</strong>.</li>
        <li>Tap <strong>Check In</strong> — the warehouse is notified instantly.</li>
        <li>Wait for your name to be called at the front desk.</li>
      </ol>
      <div class="foot">Your KIN number is confidential. Please keep it private.</div>
    </section>`;
  };

  const openPrint = (title, body) => {
    const w = window.open("", "_blank", "width=800,height=1000");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    w.document.write(`<!doctype html><html><head><title>${title}</title><style>${POSTER_STYLE}</style></head>
      <body>${body}<script>window.onload=function(){setTimeout(function(){window.print()},700)}</script></body></html>`);
    w.document.close();
  };

  const printPoster = () => {
    if (!kioskStore || !company) return;
    openPrint(`Kiosk Poster — ${kioskStore.store_name}`, posterSection(kioskStore));
  };

  const printAllPosters = () => {    const active = stores.filter((s) => s.is_active);
    if (!company || !active.length) { toast.error("No active stores to print"); return; }
    openPrint(`Kiosk Posters — ${company.company_name}`, active.map(posterSection).join(""));
  };

  const printSpotlight = async (s) => {
    if (!company) return;
    try {
      const { data } = await api.get(`/reports/spotlight/${s.id}`);
      const m = data.snapshot, live = m.live || {};
      const body = `<div class="sheet">
        <div class="brand">${company.company_name}</div>
        <h2>${m.store_name} · Performance Spotlight</h2>
        <p class="sub">${new Date().toLocaleString()}</p>
        <div class="grid">
          <div class="metric"><div class="mv">${m.served_all_time}</div><div class="ml">Served All-Time</div></div>
          <div class="metric"><div class="mv">${fmtS(m.avg_total_wait)}</div><div class="ml">Avg Total Wait</div></div>
          <div class="metric"><div class="mv">${m.within_pct_all_time}%</div><div class="ml">On Target</div></div>
          <div class="metric"><div class="mv">${live.served_today ?? 0}</div><div class="ml">Served Today</div></div>
          <div class="metric"><div class="mv">${live.waiting ?? 0}</div><div class="ml">Waiting Now</div></div>
          <div class="metric"><div class="mv">${fmtS(live.longest_active_wait ?? 0)}</div><div class="ml">Longest Active Wait</div></div>
        </div>
      </div>`;
      openPrint(`Spotlight — ${m.store_name}`, body);
    } catch (e) { toast.error(apiError(e)); }
  };

  const snapA = snapshots.find((s) => s.id === cmpA);
  const snapB = snapshots.find((s) => s.id === cmpB);
  const delta = (a, b, invert, dfmt) => {
    const d = b - a;
    if (d === 0) return { text: "±0", cls: "text-muted-foreground" };
    const good = invert ? d < 0 : d > 0;
    const mag = dfmt ? dfmt(Math.abs(d)) : Math.abs(Math.round(d * 10) / 10);
    return { text: `${d > 0 ? "+" : "−"}${mag}`, cls: good ? "text-alert-green" : "text-alert-red" };
  };

  return (
    <DashboardLayout title="Locations" needsStore={false}>
      <div className="flex items-center justify-between mb-4">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40" data-testid="location-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stores</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setShowSnapshots(true)} data-testid="snapshots-btn">
            <History size={16} /> Snapshots {snapshots.length > 0 && <span className="font-mono-t text-xs">({snapshots.length})</span>}
          </Button>
          <Button variant="outline" className="gap-2" onClick={printAllPosters} data-testid="print-all-posters-btn">
            <Printer size={16} /> Print All Posters
          </Button>
          <Button className="gap-2" onClick={() => { setForm({ ...EMPTY }); setEditing(null); }} data-testid="add-store-btn">
            <Plus size={16} /> Add Store
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((s) => (
          <div key={s.id} className="bg-card border border-border p-5" data-testid={`store-card-${s.store_code}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display text-2xl font-bold">{s.store_name}</div>
                <div className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin size={13} /> {s.town}, {s.parish}</div>
              </div>
              <span className="font-mono-t text-xs text-muted-foreground">{s.store_code}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-3 space-y-0.5">
              <div>SLA: {s.uses_company_default_sla ? "Company default" : "Custom store settings"}</div>
              <div>{s.is_active ? <span className="text-alert-green">● Active</span> : <span className="text-muted-foreground">● Inactive</span>}</div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button size="sm" variant="outline" className="gap-1" onClick={() => { setForm({ ...s }); setEditing(s.id); }} data-testid={`edit-store-${s.store_code}`}><Edit size={13} /> Edit</Button>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => setKioskStore(s)} data-testid={`kiosk-store-${s.store_code}`}><QrCode size={13} /> Kiosk</Button>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => printSpotlight(s)} data-testid={`spotlight-store-${s.store_code}`}><FileBarChart size={13} /> Spotlight</Button>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => saveSnapshot(s)} data-testid={`save-snapshot-${s.store_code}`}><Camera size={13} /> Save</Button>
              <Button size="sm" variant={s.is_active ? "outline" : "default"} className="gap-1" onClick={() => toggle(s)} data-testid={`toggle-store-${s.store_code}`}><Power size={13} /> {s.is_active ? "Deactivate" : "Activate"}</Button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit" : "Add"} Store</DialogTitle>
            <DialogDescription>Configure this location's details, service targets and status.</DialogDescription></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3 py-2">
              {[["store_name","Store Name"],["store_code","Store Code"],["parish","Parish"],["town","Town / City"],
                ["address","Street Address"],["telephone_number","Telephone"],["email_address","Email"],
                ["timezone","Timezone"],["opening_time","Opening Time"],["closing_time","Closing Time"],
                ["kiosk_identifier","Kiosk Identifier"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</label>
                  <Input className="mt-1" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`store-field-${k}`} />
                </div>
              ))}
              <div className="flex items-center gap-2 col-span-2 mt-1">
                <Switch checked={form.uses_company_default_sla} onCheckedChange={(v) => setForm({ ...form, uses_company_default_sla: v })} />
                <span className="text-sm">Use company default service targets</span>
              </div>
              <div className="flex items-center gap-2 col-span-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <span className="text-sm">Store active</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            <Button onClick={save} data-testid="save-store-btn">Save Store</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kiosk dialog */}
      <Dialog open={!!kioskStore} onOpenChange={(o) => !o && setKioskStore(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle className="font-display text-2xl">Kiosk — {kioskStore?.store_name}</DialogTitle>
            <DialogDescription>Scan or share this link to open the customer check-in kiosk for this store.</DialogDescription></DialogHeader>
          <div className="space-y-4 text-center">
            {kioskUrl && (
              <img alt="kiosk qr" className="mx-auto bg-white p-2 rounded" width={200} height={200}
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(kioskUrl)}`} />
            )}
            <div className="flex items-center gap-2 bg-secondary p-2 rounded-sm">
              <span className="font-mono-t text-xs truncate flex-1">{kioskUrl}</span>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(kioskUrl); toast.success("Copied"); }}><Copy size={14} /></Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => window.open(kioskUrl, "_blank")} data-testid="open-kiosk-btn">Open Kiosk</Button>
              <Button onClick={printPoster} data-testid="print-poster-btn">Print Poster</Button>
            </div>
            <p className="text-xs text-muted-foreground">Setup code: <span className="font-mono-t">{company?.company_code}-{kioskStore?.store_code}</span></p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Saved snapshots dialog */}
      <Dialog open={showSnapshots} onOpenChange={setShowSnapshots}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Saved Spotlight Snapshots</DialogTitle>
            <DialogDescription>Point-in-time performance records saved for each store.</DialogDescription></DialogHeader>

          {snapshots.length >= 2 && (
            <div className="border border-border bg-secondary/30 p-4 mb-2" data-testid="snapshot-compare">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Compare Two Snapshots</div>
              <div className="grid grid-cols-2 gap-3">
                <Select value={cmpA} onValueChange={setCmpA}>
                  <SelectTrigger data-testid="compare-select-a"><SelectValue placeholder="Snapshot A" /></SelectTrigger>
                  <SelectContent>
                    {snapshots.map((s) => <SelectItem key={s.id} value={s.id}>{s.store_name} · {new Date(s.created_at).toLocaleDateString()}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={cmpB} onValueChange={setCmpB}>
                  <SelectTrigger data-testid="compare-select-b"><SelectValue placeholder="Snapshot B" /></SelectTrigger>
                  <SelectContent>
                    {snapshots.map((s) => <SelectItem key={s.id} value={s.id}>{s.store_name} · {new Date(s.created_at).toLocaleDateString()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {snapA && snapB && (
                <div className="grid grid-cols-3 gap-3 mt-4 text-center" data-testid="compare-result">
                  {[
                    ["Served", snapA.served_all_time, snapB.served_all_time, (v) => v, false, null],
                    ["Avg Wait", snapA.avg_total_wait, snapB.avg_total_wait, fmtS, true, fmtS],
                    ["On Target %", snapA.within_pct_all_time, snapB.within_pct_all_time, (v) => `${v}%`, false, (v) => `${v}%`],
                  ].map(([label, a, b, fmt, invert, dfmt]) => {
                    const d = delta(a, b, invert, dfmt);
                    return (
                      <div key={label} className="bg-card border border-border p-3">
                        <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
                        <div className="flex items-center justify-center gap-2 mt-1 text-sm">
                          <span className="font-mono-t">{fmt(a)}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className="font-mono-t font-semibold">{fmt(b)}</span>
                        </div>
                        <div className={`font-display text-lg font-bold mt-1 ${d.cls}`} data-testid={`delta-${label.replace(/\s|%/g, "")}`}>{d.text}</div>
                      </div>
                    );
                  })}
                </div>
              )}
              {snapA && snapB && (
                <div className="text-[11px] text-muted-foreground text-center mt-2">
                  A: {snapA.store_name} ({new Date(snapA.created_at).toLocaleString()}) &nbsp;·&nbsp; B: {snapB.store_name} ({new Date(snapB.created_at).toLocaleString()})
                </div>
              )}
            </div>
          )}
          <div className="space-y-2 py-2" data-testid="snapshots-list">
            {snapshots.map((sn) => (
              <div key={sn.id} className="flex items-center justify-between bg-secondary/40 border border-border p-3" data-testid={`snapshot-${sn.id}`}>
                <div>
                  <div className="font-semibold">{sn.store_name} <span className="font-mono-t text-xs text-muted-foreground">{sn.store_code}</span></div>
                  <div className="text-xs text-muted-foreground">{new Date(sn.created_at).toLocaleString()} · by {sn.created_by}</div>
                </div>
                <div className="flex items-center gap-5 text-center">
                  <div><div className="font-display text-xl font-bold">{sn.served_all_time}</div><div className="text-[10px] uppercase text-muted-foreground">Served</div></div>
                  <div><div className="font-display text-xl font-bold text-alert-amber">{fmtS(sn.avg_total_wait)}</div><div className="text-[10px] uppercase text-muted-foreground">Avg Wait</div></div>
                  <div><div className="font-display text-xl font-bold text-alert-green">{sn.within_pct_all_time}%</div><div className="text-[10px] uppercase text-muted-foreground">On Target</div></div>
                </div>
              </div>
            ))}
            {snapshots.length === 0 && <div className="text-center text-muted-foreground py-8 text-sm">No snapshots saved yet. Use the "Save" button on any store to record one.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
